import { Injectable } from '@angular/core';
import {Setting} from "../models/models";
import { ConfigApiControllerService, SettingDetails } from "falcon-api";
import {map, tap} from "rxjs/operators";
import {NGXLogger} from "ngx-logger";

@Injectable({
  providedIn: 'root'
})
export class SettingService {
  private settings: Array<Setting> = [];
  versions: number[] = [1, 2, 3, 4, 5];
  languages: string[] = ['EN', 'ES', 'FR', 'JA'];

  /**
   * Constructor
   * @param configApiService Config API service instance.
   * @param logger Logger instance.
   */
  constructor(private configApiService : ConfigApiControllerService,
              private logger: NGXLogger) { }

  /**
   * Get list of global settings.
   * @param version Version to pull.
   */
  async getGlobalSettings(version : number): Promise<Array<Setting>> {
    return new Promise( (resolve, reject) => {
      let results = this.configApiService.getGlobalSettings1(version);
      results.pipe(
          map((items) => items
            .map(item => (new Setting(item.id as number, item.name as string,
                "", item.value as string, item.description as string,
                item.language as string, item.version as number
            ))),
          tap(mappedItems => this.logger.debug(JSON.stringify(mappedItems)))
        ))
        .subscribe((mappedItems) => {
          this.settings = mappedItems;
          resolve(this.settings);
        });
    });
  }

  /**
   * Get brand settings.
   * @param version Version to pull.
   * @param brandId Brand to pull settings from.
   * @param productUuid Product to pull settings from.
   */
  getBrandSettings(version: number, brandId : number, productUuid? : string): Promise<Array<Setting>> {
    return new Promise( (resolve, reject) => {
      if (productUuid != undefined) {
        var results = this.configApiService.getSettingsForProduct2(productUuid, version);
      }
      else {
        var results = this.configApiService.getSettingsForBrand1(brandId, version);
      }

      results.pipe(
        map((items) => items
            .map(item => (new Setting(item.id as number, item.name as string,
              "", item.value as string, item.description as string,
              item.language as string, item.version as number
            ))),
          tap(mappedItems => this.logger.debug(JSON.stringify(mappedItems)))
        ))
        .subscribe((mappedItems) => {
          this.settings = mappedItems;
          resolve(this.settings);
        });
    });
  }

  /**
   * Update global setting
   * @param setting Setting to update
   */
  async updateGlobalSetting(setting : Setting): Promise<boolean> {
    return new Promise( (resolve, reject) => {
      let settingDetails : SettingDetails = new SettingDetails();
      settingDetails.id = setting.id;
      settingDetails.name = setting.name;
      settingDetails.description = setting.description;
      settingDetails.value = setting.value;
      settingDetails.language = setting.language;
      settingDetails.version = setting.version;
      let settingParam : SettingDetails[] = [settingDetails];
      let results = this.configApiService.saveSettingsForGlobal(settingParam);
      results.subscribe((next) => {
        resolve(true);
      },
    error => {
        reject(false);
       });
    });
  }

  /**
   * Update brand setting or product
   * @param setting Setting to update
   * @param brandId Brand to use
   * @param productUuid Product to use
   */
  async updateBrandSetting(setting : Setting, brandId : number, productUuid? : string): Promise<boolean> {
    return new Promise( (resolve, reject) => {
      let settingDetails : SettingDetails = new SettingDetails();
      settingDetails.id = setting.id;
      settingDetails.name = setting.name;
      settingDetails.description = setting.description;
      settingDetails.value = setting.value;
      settingDetails.language = setting.language;
      settingDetails.version = setting.version;
      let settingParam : SettingDetails[] = [settingDetails];

      if (productUuid == undefined) {
        let results = this.configApiService.saveSettingsForBrand(settingParam, brandId);
        results.subscribe((next) => {
            resolve(true);
          },
          error => {
            reject(false);
          });
      } else {
        let results = this.configApiService.saveSettingsForProduct(settingParam, productUuid);
        results.subscribe((next) => {
            resolve(true);
          },
          error => {
            reject(false);
          });
      }
    });
  }

  /**
   * Delete setting
   * @param settingId Setting id to delete
   */
  async deleteSetting(settingId : number): Promise<boolean> {
    return new Promise((resolve, reject) => {
      let results = this.configApiService.deleteSettingById(settingId);
      results.subscribe((next) => {
          resolve(true);
        },
        error => {
          reject(false);
      });
    });
  }

  /**
   * Get list of setting versions available
   * @param brandId Brand to use
   * @param productId Product to use
   */

  async getSettingVersions(brandId : number, productId? : number): Promise<number[]> {
    return new Promise((resolve, reject) => {
      resolve(this.versions);
    });
  }

  /**
   * Get list of supported languages.
   */
  async getSettingLanguages(): Promise<string[]> {
    return new Promise((resolve, reject) => {
      resolve(this.languages);
    });
  }
}
